/**
 * @fileoverview Client Modal Component
 *
 * Multi-mode modal for creating and editing client records.
 * Supports Quick Add, Full Add, and Edit modes.
 *
 * Quick Add: Client Name, Email, Phone, Mailing Address (optional)
 * Full Add: + Secondary Phone, Secondary Email, Notes
 * Edit: Same as Full Add, pre-populated with existing data.
 *
 * @requires react
 * @requires react-icons/fa
 * @requires ./AdminFormModal.css
 * @version 1.0.0
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaUser,
  FaTimes,
  FaCheck,
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaPlusCircle,
} from "react-icons/fa";
import { formatPhoneDisplay, formatPhoneInput } from "../../utils/phoneUtils";
import "./AdminFormModal.css";

// =============================================================================
// CONSTANTS
// =============================================================================

/** Default empty form state */
const EMPTY_FORM = {
  clientName: "",
  email: "",
  phone: "",
  mailingAddress: "",
  secondaryEmail: "",
  secondaryPhone: "",
  clientStatus: "",
  notes: "",
};

// =============================================================================
// CLIENT MODAL COMPONENT
// =============================================================================

/**
 * ClientModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Close handler
 * @param {"quick"|"full"|"edit"} [props.mode="quick"] - Initial mode
 * @param {string} [props.recordId] - Client record ID (edit mode)
 * @param {Object} [props.clientData] - Pre-loaded client data (edit mode)
 * @param {Function} [props.onSave] - Callback after successful save
 * @param {Object} props.mutations - { createClient, updateClient } mutation objects
 */
const ClientModal = ({
  isOpen,
  onClose,
  mode: initialMode = "quick",
  recordId = null,
  clientData = null,
  onSave,
  mutations,
}) => {
  // ===================== STATE =====================

  const [currentMode, setCurrentMode] = useState(initialMode);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  /** Baseline form snapshot for detecting unsaved changes */
  const baselineRef = useRef({ ...EMPTY_FORM });

  // ===================== EFFECTS =====================

  /** Reset form when modal opens */
  useEffect(() => {
    if (!isOpen) return;

    setCurrentMode(initialMode);
    setErrors({});
    setFormError("");
    setIsSuccess(false);

    const initialForm =
      initialMode === "edit" && clientData
        ? {
            // Populate form with existing client data
            clientName: clientData.clientName || "",
            email: clientData.email || "",
            phone: formatPhoneDisplay(clientData.phone),
            mailingAddress: clientData.mailingAddress || "",
            secondaryEmail: clientData.secondaryEmail || "",
            secondaryPhone: formatPhoneDisplay(clientData.secondaryPhone),
            clientStatus: clientData.clientStatus || "",
            notes: clientData.notes || "",
          }
        : { ...EMPTY_FORM };

    setForm(initialForm);
    baselineRef.current = initialForm;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ===================== HANDLERS =====================

  /**
   * Update a single form field
   * @param {string} field - Field name
   * @param {*} value - New value
   */
  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setFormError("");
  }, []);

  /**
   * Validate the form
   * Quick Add requires: Client Name, Email, Phone, Mailing Address
   * @return {boolean} True if valid
   */
  const validate = () => {
    const newErrors = {};

    if (!form.clientName.trim()) {
      newErrors.clientName = "Client name is required";
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Phone is required";
    }

    if (!form.mailingAddress.trim()) {
      newErrors.mailingAddress = "Mailing address is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Submit the form — create or update
   */
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSaving(true);
    setFormError("");

    try {
      const payload = {
        ...form,
        email: form.email.trim().toLowerCase(),
        secondaryEmail: form.secondaryEmail
          ? form.secondaryEmail.trim().toLowerCase()
          : form.secondaryEmail,
      };

      if (currentMode === "edit" && recordId) {
        await mutations.updateClient.mutateAsync({
          clientId: recordId,
          updates: payload,
        });
      } else {
        await mutations.createClient.mutateAsync(payload);
      }

      setIsSuccess(true);

      // Auto-close after brief success display
      setTimeout(() => {
        setIsSuccess(false);
        onSave?.();
        onClose();
      }, 1200);
    } catch (error) {
      console.error("[ClientModal] Save error:", error);
      setFormError(
        error?.message || "Failed to save client. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Close the modal, warning first if there are unsaved changes.
   */
  const handleClose = () => {
    const isDirty =
      JSON.stringify(form) !== JSON.stringify(baselineRef.current);
    if (isDirty && !isSuccess) {
      if (
        !window.confirm(
          "You have unsaved changes. Are you sure you want to close?",
        )
      ) {
        return;
      }
    }
    onClose();
  };

  // ===================== RENDER GUARDS =====================

  if (!isOpen) return null;

  const showFullFields = currentMode === "full" || currentMode === "edit";
  const isEditMode = currentMode === "edit";
  const modalTitle = isEditMode
    ? "Edit Client"
    : currentMode === "full"
      ? "Add Client — Full Details"
      : "Quick Add Client";

  // ===================== RENDER =====================

  if (!isOpen) return null;

  return (
    <div className="afm-overlay" onClick={handleClose}>
      <div className="afm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="afm-header">
          <div className="afm-header-left">
            <div className="afm-header-icon">
              <FaUser />
            </div>
            <h2 className="afm-title">{modalTitle}</h2>
          </div>
          <button
            className="afm-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Mode Toggle (only for create, not edit) */}
        {!isEditMode && (
          <div className="afm-mode-toggle">
            <button
              className={`afm-mode-btn ${
                currentMode === "quick" ? "afm-mode-active" : ""
              }`}
              onClick={() => setCurrentMode("quick")}
            >
              Quick Add
            </button>
            <button
              className={`afm-mode-btn ${
                currentMode === "full" ? "afm-mode-active" : ""
              }`}
              onClick={() => setCurrentMode("full")}
            >
              Full Details
            </button>
          </div>
        )}

        {/* Success State */}
        {isSuccess ? (
          <div className="afm-body">
            <div className="afm-success">
              <div className="afm-success-icon">
                <FaCheck />
              </div>
              <h3 className="afm-success-title">
                {isEditMode ? "Client Updated!" : "Client Created!"}
              </h3>
              <p className="afm-success-message">
                {isEditMode
                  ? `${form.clientName} has been updated.`
                  : `${form.clientName} has been added.`}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Form Body */}
            <div className="afm-body">
              {/* Form Error Banner */}
              {formError && (
                <div className="afm-form-error">
                  <FaExclamationTriangle />
                  <span>{formError}</span>
                </div>
              )}

              {/* === SECTION: Basic Info === */}
              <div className="afm-section">
                <div className="afm-section-title">Client Information</div>

                <div className="afm-field">
                  <label className="afm-label">
                    Client Name{" "}
                    <span className="afm-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`afm-input ${
                      errors.clientName ? "afm-input-error" : ""
                    }`}
                    value={form.clientName}
                    onChange={(e) => handleChange("clientName", e.target.value)}
                    placeholder="Full name"
                    autoFocus
                  />
                  {errors.clientName && (
                    <div className="afm-error">{errors.clientName}</div>
                  )}
                </div>

                <div className="afm-row">
                  <div className="afm-field">
                    <label className="afm-label">
                      Email{" "}
                      <span className="afm-required">*</span>
                    </label>
                    <input
                      type="email"
                      className={`afm-input ${
                        errors.email ? "afm-input-error" : ""
                      }`}
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="client@email.com"
                    />
                    {errors.email && (
                      <div className="afm-error">{errors.email}</div>
                    )}
                  </div>
                  <div className="afm-field">
                    <label className="afm-label">
                      Phone{" "}
                      <span className="afm-required">*</span>
                    </label>
                    <input
                      type="tel"
                      className={`afm-input ${
                        errors.phone ? "afm-input-error" : ""
                      }`}
                      value={form.phone}
                      onChange={(e) =>
                        handleChange("phone", formatPhoneInput(e.target.value))
                      }
                      placeholder="(555) 555-5555"
                    />
                    {errors.phone && (
                      <div className="afm-error">{errors.phone}</div>
                    )}
                  </div>
                </div>

                <div className="afm-field">
                  <label className="afm-label">
                    Mailing Address{" "}
                    <span className="afm-required">*</span>
                  </label>
                  <textarea
                    className={`afm-textarea ${
                      errors.mailingAddress ? "afm-input-error" : ""
                    }`}
                    value={form.mailingAddress}
                    onChange={(e) =>
                      handleChange("mailingAddress", e.target.value)
                    }
                    placeholder="Street address, city, state, zip"
                    rows={2}
                  />
                  {errors.mailingAddress && (
                    <div className="afm-error">{errors.mailingAddress}</div>
                  )}
                </div>
              </div>

              {/* === FULL-ONLY SECTIONS === */}
              {showFullFields && (
                <>
                  {/* Secondary Contact */}
                  <div className="afm-section">
                    <div className="afm-section-title">
                      Secondary Contact Info
                    </div>

                    <div className="afm-row">
                      <div className="afm-field">
                        <label className="afm-label">Secondary Email</label>
                        <input
                          type="email"
                          className="afm-input"
                          value={form.secondaryEmail}
                          onChange={(e) =>
                            handleChange("secondaryEmail", e.target.value)
                          }
                          placeholder="alternate@email.com"
                        />
                      </div>
                      <div className="afm-field">
                        <label className="afm-label">Secondary Phone</label>
                        <input
                          type="tel"
                          className="afm-input"
                          value={form.secondaryPhone}
                          onChange={(e) =>
                            handleChange(
                              "secondaryPhone",
                              formatPhoneInput(e.target.value),
                            )
                          }
                          placeholder="(555) 555-5555"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Client Status */}
                  <div className="afm-section">
                    <div className="afm-section-title">Other Client Info</div>
                    <div className="afm-field">
                      <label className="afm-label">Client Status</label>
                      <select
                        className="afm-input"
                        value={form.clientStatus}
                        onChange={(e) =>
                          handleChange("clientStatus", e.target.value)
                        }
                      >
                        <option value="">Select status...</option>
                        <option value="Lead">Lead</option>
                        <option value="Active">Active</option>
                        <option value="In Warranty">In Warranty</option>
                        <option value="Former Client">Former Client</option>
                        <option value="Lead Lost">Lead Lost</option>
                      </select>
                    </div>

                    {/* Notes */}
                    <div className="afm-field">
                      <label className="afm-label">Internal Notes</label>
                      <textarea
                        className="afm-textarea"
                        value={form.notes}
                        onChange={(e) => handleChange("notes", e.target.value)}
                        placeholder="Internal notes about this client... (e.g. whose contact info is which, preferred contact method, etc.)"
                        rows={3}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="afm-footer">
              <button
                className="afm-btn afm-btn-secondary"
                onClick={handleClose}
                type="button"
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button
                className="afm-btn afm-btn-primary"
                onClick={handleSubmit}
                disabled={isSaving}
                type="button"
                aria-label="Save"
              >
                {isSaving ? (
                  <>
                    <FaSpinner className="spinning" />
                    Saving...
                  </>
                ) : isEditMode ? (
                  <>
                    <FaCheckCircle /> Save Changes
                  </>
                ) : (
                  <>
                    <FaPlusCircle /> Create Client
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClientModal;
